import { useState } from 'react';
import { isValidNickname, isValidLocation, isNotFutureDate, ValidationErrorMessages } from '../../utils/validation';
import { generatePlantNames } from '../../utils/plantNameGenerator';

export interface PlantFormData {
  nickname: string;
  location: string;
  acquiredDate: Date | null;
}

interface PlantDetailsFormProps {
  commonName: string;
  scientificName: string;
  photoUrl: string;
  onSubmit: (data: PlantFormData) => void;
  onCancel: () => void;
  loading?: boolean;
}

export function PlantDetailsForm({
  commonName,
  scientificName,
  photoUrl,
  onSubmit,
  onCancel,
  loading,
}: PlantDetailsFormProps) {
  const [nickname, setNickname] = useState('');
  const [location, setLocation] = useState('');
  const [acquiredDate, setAcquiredDate] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [nameSuggestions, setNameSuggestions] = useState<string[]>([]);

  const handleSuggestNames = () => {
    const names = generatePlantNames({ commonName, scientificName }, 5);
    setNameSuggestions(names);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    if (nickname && !isValidNickname(nickname)) {
      newErrors.nickname = ValidationErrorMessages.NICKNAME_TOO_LONG;
    }

    if (location && !isValidLocation(location)) {
      newErrors.location = ValidationErrorMessages.LOCATION_TOO_LONG;
    }

    const parsedDate = acquiredDate ? new Date(acquiredDate) : null;
    if (parsedDate && !isNotFutureDate(parsedDate)) {
      newErrors.acquiredDate = ValidationErrorMessages.ACQUIRED_DATE_FUTURE;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit({
      nickname: nickname.trim(),
      location: location.trim(),
      acquiredDate: parsedDate,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Plant preview */}
      <div className="flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-4">
        <div className="h-16 w-16 overflow-hidden rounded-full bg-gray-100">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={commonName}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <span className="material-symbols-outlined text-gray-300" style={{ fontSize: 24 }} aria-hidden="true">
                eco
              </span>
            </div>
          )}
        </div>
        <div>
          <h2 className="font-display font-bold text-editorial-black">{commonName}</h2>
          {scientificName && (
            <p className="text-sm font-serif italic text-gray-500">{scientificName}</p>
          )}
        </div>
      </div>

      {/* Form fields — editorial underline inputs */}
      <div className="space-y-6">
        {/* Nickname */}
        <div className="group">
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="nickname" className="section-header block group-focus-within:text-editorial-black transition-colors">
              Nickname <span className="text-gray-400 normal-case tracking-normal">(optional)</span>
            </label>
            <button
              type="button"
              onClick={handleSuggestNames}
              className="flex items-center gap-1 text-xs font-sans font-bold text-primary uppercase tracking-wider hover:brightness-90 transition-colors"
              aria-label={nameSuggestions.length > 0 ? 'Refresh name suggestions' : 'Suggest nicknames'}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }} aria-hidden="true">
                {nameSuggestions.length > 0 ? 'refresh' : 'auto_awesome'}
              </span>
              <span>{nameSuggestions.length > 0 ? 'More' : 'Suggest'}</span>
            </button>
          </div>
          <input
            id="nickname"
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="e.g., Fern-ando"
            maxLength={50}
            className="input-editorial"
          />
          {nameSuggestions.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {nameSuggestions.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => {
                    setNickname(name);
                    setNameSuggestions([]);
                  }}
                  className="px-3 py-1.5 min-h-[44px] text-sm font-sans rounded-full border border-gray-200 bg-white text-editorial-black hover:border-primary hover:bg-primary/5 transition-colors"
                >
                  {name}
                </button>
              ))}
            </div>
          )}
          {errors.nickname && (
            <p className="mt-1 text-sm font-sans text-danger">{errors.nickname}</p>
          )}
        </div>

        {/* Location */}
        <div className="group">
          <label htmlFor="location" className="section-header mb-1 block group-focus-within:text-editorial-black transition-colors">
            Location <span className="text-gray-400 normal-case tracking-normal">(optional)</span>
          </label>
          <input
            id="location"
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g., Living Room, Kitchen Window"
            maxLength={100}
            className="input-editorial"
          />
          {errors.location && (
            <p className="mt-1 text-sm font-sans text-danger">{errors.location}</p>
          )}
        </div>

        {/* Acquired date */}
        <div className="group">
          <label htmlFor="acquiredDate" className="section-header mb-1 block group-focus-within:text-editorial-black transition-colors">
            When did you get this plant? <span className="text-gray-400 normal-case tracking-normal">(optional)</span>
          </label>
          <div className="flex items-center gap-2">
            <input
              id="acquiredDate"
              type="date"
              value={acquiredDate}
              onChange={(e) => setAcquiredDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="input-editorial flex-1"
            />
            {acquiredDate && (
              <button
                type="button"
                onClick={() => setAcquiredDate('')}
                className="flex items-center justify-center w-10 h-10 rounded-full text-gray-400 hover:text-editorial-black hover:bg-gray-100 transition-colors"
                aria-label="Clear date"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden="true">close</span>
              </button>
            )}
          </div>
          {errors.acquiredDate && (
            <p className="mt-1 text-sm font-sans text-danger">{errors.acquiredDate}</p>
          )}
        </div>
      </div>

      {/* Buttons */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="flex-1 h-12 rounded-lg border border-gray-300 font-sans font-bold text-xs uppercase tracking-widest text-editorial-black transition-colors hover:border-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex flex-1 h-12 items-center justify-center gap-2 rounded-lg bg-editorial-black text-white font-sans font-bold text-xs uppercase tracking-widest transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <span>Save Plant</span>
              <span className="material-symbols-outlined" style={{ fontSize: 18 }} aria-hidden="true">
                arrow_forward
              </span>
            </>
          )}
        </button>
      </div>
    </form>
  );
}
